import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useOnSuccessCacheEntriesById } from 'features/knowledgeDocumentViewer/hooks/useRolodexEntriesCachedById'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { parseSystemDocumentEntries } from 'features/knowledgeDocumentViewer/types/systemDocumentEntrySchemas'
import { systemDocumentByEntryId } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { isRolodexBrowseInvalidationSWRKey } from 'features/knowledgeDocumentViewer/utils/invalidateRolodexEntryLoads'
import { useGeckoNotifications } from 'features/notifications'
import {
  useGetRolodexEntriesByLoadOptions,
  useRolodexEntries,
  useRolodexHistogram,
} from 'features/rolodex'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useSWRConfig } from 'swr'
import {
  RolodexEntryType,
  RolodexHistogramQuery,
  RolodexHistogramResponse,
} from 'types'

export type DocumentMetadataUpdate = {
  title?: string
  system?: string
  vendor_name?: string
  document_type?: string
  drawing_number?: string
  description?: string
  revision_code?: string
  revision_date?: string
}

interface KnowledgeDocumentDataContextType {
  systemDocumentEntries: SystemDocumentEntry[]
  updateDocumentMetadata: (
    documentId: string,
    metadata: DocumentMetadataUpdate
  ) => Promise<void>
  orgID: string
  siteID: string
  showDocumentOcrBoxes: boolean
  /** True while the deferred full site list is requested and in flight. */
  isLoadingDocuments: boolean
  /** True after browse-all (or another caller) has loaded the full site list. */
  hasFullSystemDocumentList: boolean
  requestFullSystemDocumentList: () => void
  uniqueAssetHistogram?: RolodexHistogramResponse[]
  allAssetHistogram?: RolodexHistogramResponse[]
}

interface KnowledgeDocumentDataProviderProps {
  children: React.ReactNode
  siteID: string
  orgID: string
}

export const KnowledgeDocumentDataProvider: React.FC<
  KnowledgeDocumentDataProviderProps
> = ({ children, siteID, orgID }) => {
  const { rolodexPatch } = useRolodexEdit()
  const { successNotification, errorNotification } = useGeckoNotifications()
  const { authorizedAxios } = useGeckoAuth()
  const { mutate } = useSWRConfig()
  const { ROLODEX_URL } = useRuntimeVariables()
  const {
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    pipelineConfigCacheScope,
  } = usePipelineConfig()
  const onSuccessCacheEntriesById =
    useOnSuccessCacheEntriesById<SystemDocumentEntry>(pipelineConfigCacheScope)
  const [needsFullSystemDocumentList, setNeedsFullSystemDocumentList] =
    useState(false)

  const requestFullSystemDocumentList = useCallback(() => {
    setNeedsFullSystemDocumentList(true)
  }, [])

  const siteConfigQuery = useMemo(
    () => ({
      type: [RolodexEntryType.SITE_CONFIG],
      tags: { site: siteID },
      limit: 1,
    }),
    [siteID]
  )
  const { siteConfigs } = useRolodexEntries(siteConfigQuery)
  const showDocumentOcrBoxes =
    siteConfigs[0]?.data.showDocumentOcrBoxes ?? false

  const systemDocumentLoadOptions = useMemo(() => {
    if (!needsFullSystemDocumentList || isPipelineConfigScopeLoading) {
      return undefined
    }

    const scopedTags = getPipelineConfigScopedTags({ site: siteID })
    if (!scopedTags) return undefined

    return {
      type: [RolodexEntryType.SYSTEM_DOCUMENT],
      tags: scopedTags,
      limit: 1000000,
    }
  }, [
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    needsFullSystemDocumentList,
    siteID,
  ])
  const {
    data: systemDocumentEntries,
    mutate: refreshSystemDocuments,
    isLoading: isLoadingFullSystemDocumentList,
  } = useGetRolodexEntriesByLoadOptions<SystemDocumentEntry[]>(
    systemDocumentLoadOptions,
    { onSuccess: onSuccessCacheEntriesById }
  )

  const hasFullSystemDocumentList =
    needsFullSystemDocumentList && systemDocumentEntries !== undefined
  const isLoadingDocuments =
    needsFullSystemDocumentList &&
    (isLoadingFullSystemDocumentList || systemDocumentEntries === undefined)

  // Units are pipeline-config-agnostic; a config tag here matches nothing.
  const uniqueAssetHistogramQuery = useMemo<RolodexHistogramQuery | undefined>(
    () =>
      ({
        type: RolodexEntryType.UNIT,
        select: [
          {
            field: 'data',
            key: 'custom_fields.0.inputValue',
            label: 'asset_type',
            group_by: true,
          },
          {
            op: 'count',
            field: 'id',
          },
        ],
        tags: { site: siteID },
      }) as RolodexHistogramQuery,
    [siteID]
  )
  const { data: uniqueAssetHistogramData } = useRolodexHistogram(
    uniqueAssetHistogramQuery
  )

  const allAssetHistogramQuery = useMemo<
    RolodexHistogramQuery | undefined
  >(() => {
    if (isPipelineConfigScopeLoading) {
      return undefined
    }

    const scopedTags = getPipelineConfigScopedTags({ site: siteID })
    if (!scopedTags) return undefined

    return {
      type: RolodexEntryType.KNOWLEDGE_DOCUMENT,
      links: [RolodexEntryType.UNIT],
      select: [
        {
          type: RolodexEntryType.UNIT,
          field: 'data',
          key: 'custom_fields.0.inputValue',
          label: 'asset_type',
          group_by: true,
        },
        {
          op: 'count',
          field: 'id',
        },
      ],
      tags: scopedTags,
    } as RolodexHistogramQuery
  }, [getPipelineConfigScopedTags, isPipelineConfigScopeLoading, siteID])
  const { data: allAssetHistogramData } = useRolodexHistogram(
    allAssetHistogramQuery
  )

  const uniqueAssetHistogram = useMemo(() => {
    return uniqueAssetHistogramData
  }, [uniqueAssetHistogramData])

  const allAssetHistogram = useMemo(() => {
    return allAssetHistogramData
  }, [allAssetHistogramData])

  const loadSystemDocumentByEntryId = useCallback(
    async (entryId: string): Promise<SystemDocumentEntry | undefined> => {
      const scopedTags = getPipelineConfigScopedTags({ site: siteID })
      if (!scopedTags) return undefined

      const response = await authorizedAxios.post<unknown>(
        `${ROLODEX_URL}/api/v2/entries/load`,
        systemDocumentByEntryId(entryId, scopedTags)
      )
      return parseSystemDocumentEntries(response.data)[0]
    },
    [ROLODEX_URL, authorizedAxios, getPipelineConfigScopedTags, siteID]
  )

  const updateDocumentMetadata = useCallback(
    async (documentId: string, metadata: DocumentMetadataUpdate) => {
      let documentEntry = systemDocumentEntries?.find(
        (entry) => entry.entry.id === documentId
      )

      if (!documentEntry) {
        documentEntry = await loadSystemDocumentByEntryId(documentId)
      }

      if (!documentEntry) {
        errorNotification(t`Document not found`)
        return
      }

      try {
        await rolodexPatch([
          {
            id: documentId,
            data: {
              ...documentEntry.entry.data,
              ...metadata,
            },
          },
        ])
        if (hasFullSystemDocumentList) {
          await refreshSystemDocuments()
        }
        await mutate(isRolodexBrowseInvalidationSWRKey)
        successNotification(t`Document metadata updated`)
      } catch (error) {
        console.error('Error updating document metadata:', error)
        errorNotification(t`Failed to update document metadata`)
        throw error
      }
    },
    [
      errorNotification,
      hasFullSystemDocumentList,
      loadSystemDocumentByEntryId,
      mutate,
      refreshSystemDocuments,
      rolodexPatch,
      successNotification,
      systemDocumentEntries,
    ]
  )

  const contextValue: KnowledgeDocumentDataContextType = {
    systemDocumentEntries: systemDocumentEntries ?? [],
    updateDocumentMetadata,
    orgID,
    siteID,
    showDocumentOcrBoxes,
    isLoadingDocuments,
    hasFullSystemDocumentList,
    requestFullSystemDocumentList,
    uniqueAssetHistogram,
    allAssetHistogram,
  }

  return (
    <KnowledgeDocumentDataContext.Provider value={contextValue}>
      {children}
    </KnowledgeDocumentDataContext.Provider>
  )
}

const KnowledgeDocumentDataContext = createContext<
  KnowledgeDocumentDataContextType | undefined
>(undefined)

const rejectDocumentMetadataUpdate = (): Promise<void> =>
  Promise.reject(new Error('Document metadata is read-only in this view'))

const ignoreFullSystemDocumentListRequest = (): void => {}

export const KnowledgeDocumentSiteProvider: React.FC<
  KnowledgeDocumentDataProviderProps
> = ({ children, siteID, orgID }) => {
  const contextValue = useMemo<KnowledgeDocumentDataContextType>(
    () => ({
      systemDocumentEntries: [],
      updateDocumentMetadata: rejectDocumentMetadataUpdate,
      orgID,
      siteID,
      showDocumentOcrBoxes: false,
      isLoadingDocuments: false,
      hasFullSystemDocumentList: false,
      requestFullSystemDocumentList: ignoreFullSystemDocumentListRequest,
    }),
    [orgID, siteID]
  )
  return (
    <KnowledgeDocumentDataContext.Provider value={contextValue}>
      {children}
    </KnowledgeDocumentDataContext.Provider>
  )
}

export const useKnowledgeDocumentDataContext =
  (): KnowledgeDocumentDataContextType => {
    const context = useContext(KnowledgeDocumentDataContext)
    if (context === undefined) {
      throw new Error(
        'useKnowledgeDocumentDataContext must be used within a KnowledgeDocumentDataProvider'
      )
    }
    return context
  }
